import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import * as React from 'react';
import { MinusIcon, PlusIcon } from '@/imports/ui/chakra/feather';

import { Abbreviate, AbbreviateContent, AbbreviateToggle } from '@/components/Abbreviate';
import { CurrencyDisplay } from '@/components/Currency';
import { IEventItemsForCart } from '@/imports/ui/components/Cart/TypeDefs';
import { TicketSelect_EventFragment_ticketsCurrentUserCanPurchase } from '../ticketTypes';
import { useCurrent } from '@/hooks/useCurrent';

interface TicketItemProps {
  ticket: IEventItemsForCart;
  eligibility: { isEligible: boolean; reason?: string };
  audienceLabel: string;
  badgeColor: string;
  isFree: boolean;
  isRegistered?: boolean;
  hasCheckoutDetails?: boolean;
  onDecrement: (id: string) => void;
  onIncrement: (id: string, name: string, label: string) => void;
}

export const TicketItem: React.FC<TicketItemProps> = ({
  ticket,
  eligibility,
  audienceLabel,
  badgeColor,
  isFree,
  isRegistered = false,
  hasCheckoutDetails = false,
  onDecrement,
  onIncrement,
}) => {
  const { user } = useCurrent();
  const baseTicket = ticket as unknown as TicketSelect_EventFragment_ticketsCurrentUserCanPurchase;
  const isMagicLinkTicket = baseTicket?.canPurchase?.type === 'MAGIC_LINK';
  const isAuthenticated = !!user;
  
  // For magic link tickets, show warning if not authenticated
  const shouldShowAuthWarning = isMagicLinkTicket && !isAuthenticated;
  
  return (
    <Box
      border="1px"
      borderColor={!eligibility.isEligible ? '#DC3545' : '#F5F5F5'}
      borderRadius="md"
      p={3}
      mb={3}
      bg={!eligibility.isEligible ? '#F5F5F5' : '#FFFFFF'}
      opacity={!eligibility.isEligible ? 0.7 : 1}
    >
      <Stack spacing={2}>
        <Flex justifyContent="space-between" align="start" gap={3}>
          <Box flex={1} minW={0}>
            <Text
              textStyle="xlBodyStrong"
              mb={1}
              sx={{
                overflowWrap: 'anywhere',
                wordBreak: 'normal',
              }}
            >
              {baseTicket.name}
            </Text>

            <HStack spacing={2} mb={2} flexWrap="wrap">
              <Badge colorScheme={badgeColor} fontSize="xs">
                {audienceLabel}
              </Badge>
              {isRegistered && (
                <Badge colorScheme="green" fontSize="xs">
                  Registered
                </Badge>
              )}
              {!isRegistered && hasCheckoutDetails && (
                <Badge colorScheme="orange" fontSize="xs">
                  Form Saved
                </Badge>
              )}
            </HStack>

            {isFree ? (
              <Text fontWeight="bold" color="#2C8530">
                Free
              </Text>
            ) : (
              <Text textStyle="bodyStrong" color="#1676CE">
                <CurrencyDisplay value={dinero(baseTicket.price)} />
              </Text>
            )}
          </Box>

          {eligibility.isEligible &&
            (ticket.quantity <= 0 ? (
              <Button
                aria-label={`Add ${baseTicket.name}`}
                size="sm"
                colorScheme="primary"
                onClick={() => onIncrement(ticket.itemId, baseTicket.name, audienceLabel)}
                data-testid={`add-${ticket.itemId}`}
                flexShrink={0}
                isDisabled={shouldShowAuthWarning}
              >
                <PlusIcon boxSize={3} mr={1} />
                ADD
              </Button>
            ) : (
              <HStack spacing={2} bg="#F5F5F5" p={2} borderRadius="md" flexShrink={0}>
                {ticket.quantity >= 1 && (
                  <IconButton
                    colorScheme="primary"
                    variant="ghost"
                    aria-label={`Decrease quantity of ${baseTicket.name}`}
                    icon={<MinusIcon boxSize={3} />}
                    size="sm"
                    disabled={ticket.quantity < 1}
                    onClick={() => onDecrement(ticket.itemId)}
                    data-testid={`decrease-quantity-${ticket.itemId}`}
                  />
                )}
                <Text textStyle="bodyStrong" minW="24px" textAlign="center">
                  {ticket.quantity}
                </Text>
                <IconButton
                  colorScheme="primary"
                  variant="ghost"
                  aria-label={`Increase quantity of ${baseTicket.name}`}
                  icon={<PlusIcon boxSize={3} />}
                  size="sm"
                  disabled={ticket.quantity >= 20 || baseTicket.canPurchase?.type === 'MAGIC_LINK'}
                  onClick={() => onIncrement(ticket.itemId, baseTicket.name, audienceLabel)}
                  data-testid={`increase-quantity-${ticket.itemId}`}
                />
              </HStack>
            ))}
        </Flex>

        {baseTicket.description && (
        <Abbreviate spacing="1" noOfLines={2}>
          <AbbreviateContent as={Text} textStyle="compact" color="#333333">
            {baseTicket.description}
          </AbbreviateContent>

          <span
            onClick={(event) => {
              event.preventDefault();
            }}
          >
            <AbbreviateToggle size="sm" />
          </span>
        </Abbreviate>
      )}

      {/* Show authentication warning for magic link tickets */}
      {shouldShowAuthWarning && (
        <Alert status="warning" borderRadius="md" size="sm">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Please sign in or create an account to continue.
          </AlertDescription>
        </Alert>
      )}

      {/* Show other eligibility warnings */}
      {!eligibility.isEligible && !shouldShowAuthWarning && (
        <Alert status="warning" borderRadius="md" size="sm">
          <AlertIcon />
          <AlertDescription fontSize="sm">{eligibility.reason}</AlertDescription>
        </Alert>
      )}
    </Stack>
  </Box>
  );
};
